import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, Space, Typography, Breadcrumb, Upload, message, Row, Col, Spin, Radio, Table } from 'antd';
import { 
  FolderOutlined, 
  UploadOutlined, 
  PlusOutlined,
  ArrowLeftOutlined,
  FileTextOutlined,
  BankOutlined,
  DollarOutlined,
  ToolOutlined,
  ShopOutlined,
  UserOutlined,
  HomeOutlined,
  AppstoreOutlined,
  UnorderedListOutlined
} from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  getFolderContents, 
  uploadDocumentToFolder 
} from '@/api/documents';
import { useAuth } from '@/contexts/AuthContext';

const { Title, Text } = Typography;
const { Button: RadioButton } = Radio;

type ViewMode = 'folder' | 'list';

interface FolderItem {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  type: 'folder' | 'file';
  path: string[];
  fileCount: number;
  children?: FolderItem[];
}

interface FolderBrowserProps {
  onFileSelect?: (filePath: string, fileName: string) => void;
}

const FolderBrowser: React.FC<FolderBrowserProps> = () => {
  const { t } = useTranslation(['documents', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<FolderItem | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('folder');
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [subfolderContents, setSubfolderContents] = useState<Map<string, FolderItem[]>>(new Map());

  // Fetch folder contents
  const { data: folders, isLoading } = useQuery({
    queryKey: ['folderContents', firestoreUser?.workspace?.id, firestoreUser?.workspace?.slug, currentPath.join('/')],
    queryFn: () => getFolderContents(
      firestoreUser?.workspace?.id || '',
      firestoreUser?.workspace?.slug || '',
      currentPath.join('/')
    ),
    enabled: !!firestoreUser?.workspace?.id && !!firestoreUser?.workspace?.slug,
  });

  // Function to create responsive folder icons (same as Administrator tab)
  const createFolderIcon = (backgroundColor: string, borderColor: string, icon: React.ReactNode, size: 'large' | 'small' = 'large') => {
    const isSmall = size === 'small';
    const maxWidth = isSmall ? '32px' : '80px';
    const tabWidth = isSmall ? '12px' : 'clamp(24px, 8vw, 32px)';
    const tabHeight = isSmall ? '6px' : 'clamp(12px, 4vw, 16px)';
    const fontSize = isSmall ? '12px' : 'clamp(14px, 4vw, 20px)';
    const tabLeft = isSmall ? '4px' : '8px';
    const tabTop = isSmall ? '-4px' : '-8px';
    
    return (
      <div style={{ position: 'relative', display: 'inline-block', width: isSmall ? '32px' : '100%' }}>
        <div style={{ 
          width: isSmall ? '32px' : '100%', 
          maxWidth,
          height: 'auto',
          aspectRatio: '4/3',
          backgroundColor, 
          borderRadius: '4px 4px 0 0',
          position: 'relative',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
          border: `1px solid ${borderColor}`,
          margin: '0 auto'
        }}>
          {/* Folder tab */}
          <div style={{
            position: 'absolute',
            top: tabTop,
            left: tabLeft,
            width: tabWidth,
            height: tabHeight,
            backgroundColor,
            borderRadius: '4px 4px 0 0',
            border: `1px solid ${borderColor}`,
            borderBottom: 'none'
          }} />
          {/* Icon overlay */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize,
            color: 'inherit'
          }}>
            {icon}
          </div>
        </div>
      </div>
    );
  };

  // Get folder icon based on folder name (supports localized names)
  const getFolderIcon = (folderName: string, size: 'large' | 'small' = 'large') => {
    const name = folderName.toLowerCase();
    
    // Check for Administrator/Administración/Administrador
    if (name.includes('administrator') || name.includes('administración') || name.includes('administrador')) {
      return createFolderIcon('#ffd700', '#d4af37', <HomeOutlined style={{ color: '#52c41a' }} />, size);
    } 
    // Check for Properties/Propiedades
    else if (name.includes('properties') || name.includes('propiedades')) {
      return createFolderIcon('#87ceeb', '#5f9ea0', <HomeOutlined style={{ color: '#1890ff' }} />, size);
    } 
    // Check for Resident/Residente
    else if (name.includes('resident') || name.includes('residente')) {
      return createFolderIcon('#98fb98', '#90ee90', <UserOutlined style={{ color: '#13c2c2' }} />, size);
    } 
    // Check for Employee/Empleado
    else if (name.includes('employee') || name.includes('empleado')) {
      return createFolderIcon('#ffa07a', '#ff7f50', <UserOutlined style={{ color: '#fa8c16' }} />, size);
    } 
    // Check for Vendor/Proveedor
    else if (name.includes('vendor') || name.includes('proveedor')) {
      return createFolderIcon('#f0e68c', '#daa520', <ShopOutlined style={{ color: '#eb2f96' }} />, size);
    } 
    // Check for Asset/Activo
    else if (name.includes('asset') || name.includes('activo')) {
      return createFolderIcon('#dda0dd', '#ba55d3', <ToolOutlined style={{ color: '#52c41a' }} />, size);
    } 
    // Check for Banking/Bancario
    else if (name.includes('banking') || name.includes('bancario')) {
      return createFolderIcon('#87ceeb', '#5f9ea0', <BankOutlined style={{ color: '#1890ff' }} />, size);
    } 
    // Check for Finance/Finanzas
    else if (name.includes('finance') || name.includes('finanças') || name.includes('finanzas')) {
      return createFolderIcon('#ffd700', '#d4af37', <DollarOutlined style={{ color: '#52c41a' }} />, size);
    } 
    // Default folder icon
    else {
      return createFolderIcon('#d9d9d9', '#bfbfbf', <FolderOutlined style={{ color: '#8c8c8c' }} />, size);
    }
  };

  // Convert Firebase folders to FolderItem format
  const folderItems: FolderItem[] = React.useMemo(() => {
    if (!folders) return [];

    return folders.map((folder) => ({
      id: folder.path,
      name: folder.name,
      icon: getFolderIcon(folder.name),
      description: '', // Remove the file count description
      type: 'folder' as const,
      path: currentPath.concat([folder.name]),
      fileCount: folder.files,
      children: folder.subfolders.map(subfolder => ({
        id: `${folder.path}/${subfolder}`,
        name: subfolder,
        icon: createFolderIcon('#e6f7ff', '#91d5ff', <FolderOutlined style={{ color: '#1890ff' }} />),
        description: '', // Remove description for subfolders too
        type: 'folder' as const,
        path: currentPath.concat([folder.name, subfolder]),
        fileCount: 0,
      }))
    }));
  }, [folders, currentPath]);

  // Handle folder click
  const handleFolderClick = (folder: FolderItem) => {
    if (viewMode === 'folder') {
      // In folder view, navigate to the folder
      if (folder.type === 'folder') {
        setCurrentPath(folder.path);
        setSelectedFolder(folder);
      }
    } else {
      // In list view, toggle expansion
      if (folder.type === 'folder') {
        toggleFolderExpansion(folder.id);
      }
    }
  };

  const toggleFolderExpansion = async (folderId: string) => {
    const isCurrentlyExpanded = expandedFolders.has(folderId);
    
    if (!isCurrentlyExpanded) {
      // Expanding - fetch subfolder contents
      const folder = folderItems.find(item => item.id === folderId);
      if (folder && folder.type === 'folder') {
        try {
          const subfolderPath = folder.path.join('/');
          const subfolderContents = await getFolderContents(
            firestoreUser?.workspace?.id || '',
            firestoreUser?.workspace?.slug || '',
            subfolderPath
          );
          
          // Convert to FolderItem format
          const subfolderItems: FolderItem[] = subfolderContents.map((subfolder) => ({
            id: subfolder.path,
            name: subfolder.name,
            icon: getFolderIcon(subfolder.name, 'small'),
            description: '',
            type: 'folder' as const,
            path: folder.path.concat([subfolder.name]),
            fileCount: subfolder.files,
            children: subfolder.subfolders.map(subsubfolder => ({
              id: `${subfolder.path}/${subsubfolder}`,
              name: subsubfolder,
              icon: getFolderIcon(subsubfolder, 'small'),
              description: '',
              type: 'folder' as const,
              path: folder.path.concat([subfolder.name, subsubfolder]),
              fileCount: 0,
              children: []
            }))
          }));
          
          // Store the subfolder contents
          setSubfolderContents(prev => new Map(prev).set(folderId, subfolderItems));
        } catch (error) {
          console.error('Error fetching subfolder contents:', error);
          message.error('Failed to load folder contents');
        }
      }
    }
    
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  // Handle breadcrumb click
  const handleBreadcrumbClick = (path: string[]) => {
    setCurrentPath(path);
    if (path.length === 0) {
      setSelectedFolder(null);
    } else {
      const folder = folderItems.find(f => f.path.join('/') === path.join('/'));
      setSelectedFolder(folder || null);
    }
  };

  // Handle file upload
  const handleUpload = async (file: File) => {
    if (currentPath.length === 0) {
      message.error(t('documents:upload.selectFolderFirst'));
      return;
    }

    try {
      const parentFolder = currentPath[0];
      const subfolder = currentPath.length > 1 ? currentPath.slice(1).join('/') : undefined;
      
      await uploadDocumentToFolder({
        file,
        workspaceId: firestoreUser?.workspace?.id || '',
        workspaceSlug: firestoreUser?.workspace?.slug || '',
        folder: parentFolder,
        subfolder,
        category: 'general',
        description: `Uploaded to ${currentPath.join('/')} folder`,
        language: (firestoreUser?.workspace as any)?.language || 'en'
      });

      message.success(t('documents:upload.success'));
      queryClient.invalidateQueries({ queryKey: ['folderContents'] });
    } catch (error) {
      console.error('Upload error:', error);
      message.error(t('documents:upload.error'));
    }
  };

  // Render folder card with fixed height
  const renderFolderCard = (item: FolderItem) => (
    <Col xs={12} sm={8} md={6} lg={4} xl={3} key={item.id}>
      <Card
        hoverable
        style={{ 
          marginBottom: 16, 
          cursor: 'pointer',
          textAlign: 'center',
          height: '140px', // Fixed height for all cards
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={() => handleFolderClick(item)}
        bodyStyle={{ 
          padding: '12px 8px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
          flex: 1
        }}
      >
        <div style={{ 
          flex: '0 0 auto', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          marginBottom: '8px'
        }}>
          {item.icon}
        </div>
        <div style={{ 
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: 0 // Allow shrinking
        }}>
          <Title level={5} style={{ 
            margin: '0',
            fontSize: 'clamp(11px, 3vw, 13px)',
            lineHeight: '1.2',
            wordBreak: 'break-word',
            display: '-webkit-box',
            WebkitLineClamp: 2, // Limit to 2 lines
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {item.name}
          </Title>
          {item.description && (
            <Text type="secondary" style={{ 
              fontSize: 'clamp(9px, 2.5vw, 11px)', 
              lineHeight: '1.3',
              display: '-webkit-box',
              WebkitLineClamp: 1, // Limit to 1 line
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginTop: '4px'
            }}>
              {item.description}
            </Text>
          )}
        </div>
      </Card>
    </Col>
  );

  // Render breadcrumb (same as Administrator tab)
  const renderBreadcrumb = () => {
    if (currentPath.length === 0) return null;

    const breadcrumbItems = [
      {
        title: (
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />}
            onClick={() => handleBreadcrumbClick([])}
            style={{ padding: 0 }}
          >
            {t('documents:folderBrowser.title')}
          </Button>
        )
      },
      ...currentPath.map((path, index) => ({
        title: (
          <Button
            key={index}
            type="text"
            onClick={() => handleBreadcrumbClick(currentPath.slice(0, index + 1))}
            style={{ padding: 0 }}
          >
            {path}
          </Button>
        )
      }))
    ];

    return (
      <Breadcrumb 
        items={breadcrumbItems}
        style={{ marginBottom: 24 }}
      />
    );
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          <Text type="secondary">{t('common:loading')}</Text>
        </div>
      </div>
    );
  }

  // Build flat list structure for list view (no nested children)
  const buildFlatListData = (items: FolderItem[]): any[] => {
    const result: any[] = [];
    
    items.forEach(item => {
      const isExpanded = expandedFolders.has(item.id);
      
      // Add the main item
      result.push({
        key: item.id,
        id: item.id,
        name: item.name,
        type: item.type,
        fileCount: item.fileCount,
        path: item.path,
        icon: getFolderIcon(item.name, 'small'),
        isExpanded,
        hasChildren: item.type === 'folder',
        level: 0
      });
      
      // If expanded, add subfolder contents as separate rows
      if (isExpanded && item.type === 'folder') {
        const fetchedContents = subfolderContents.get(item.id);
        if (fetchedContents && fetchedContents.length > 0) {
          fetchedContents.forEach(subfolder => {
            result.push({
              key: subfolder.id,
              id: subfolder.id,
              name: subfolder.name,
              type: subfolder.type,
              fileCount: subfolder.fileCount,
              path: subfolder.path,
              icon: getFolderIcon(subfolder.name, 'small'),
              isExpanded: expandedFolders.has(subfolder.id),
              hasChildren: subfolder.type === 'folder',
              level: 1,
              parentId: item.id
            });
          });
        } else {
          // Show empty folder message
          result.push({
            key: `${item.id}-empty`,
            id: `${item.id}-empty`,
            name: t('documents:emptyFolder'),
            type: 'empty',
            fileCount: 0,
            path: item.path,
            icon: null,
            isExpanded: false,
            hasChildren: false,
            level: 1,
            parentId: item.id,
            isEmptyMessage: true
          });
        }
      }
    });
    
    return result;
  };

  // Render list view
  const renderListView = () => {
    const flatData = buildFlatListData(folderItems);
    
    const columns = [
      {
        title: t('documents:table.name'),
        dataIndex: 'name',
        key: 'name',
        render: (text: string, record: any) => {
          // Special handling for empty folder message
          if (record.isEmptyMessage) {
            return (
              <Space>
                <span style={{ width: '16px', display: 'inline-block' }} />
                <span style={{ color: '#999', fontStyle: 'italic' }}>{text}</span>
              </Space>
            );
          }
          
          // Add indentation based on level
          const indentStyle = {
            paddingLeft: `${record.level * 20}px`
          };
          
          return (
            <div style={indentStyle}>
              <Space>
                {record.type === 'folder' && (
                  <span
                    style={{ 
                      width: '16px', 
                      height: '16px', 
                      fontSize: '14px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                  >
                    {record.isExpanded ? '−' : '+'}
                  </span>
                )}
                {record.type !== 'folder' && <span style={{ width: '16px', display: 'inline-block' }} />}
                {record.icon}
                <span>{text}</span>
              </Space>
            </div>
          );
        },
      },
      {
        title: t('documents:table.type'),
        dataIndex: 'type',
        key: 'type',
        render: (type: string, record: any) => {
          if (record.isEmptyMessage) {
            return <span style={{ color: '#999' }}>-</span>;
          }
          return <span style={{ textTransform: 'capitalize' }}>{type}</span>;
        },
      },
      {
        title: t('documents:table.files'),
        dataIndex: 'fileCount',
        key: 'fileCount',
        render: (count: number, record: any) => {
          if (record.isEmptyMessage) {
            return <span style={{ color: '#999' }}>-</span>;
          }
          return `${count} ${count === 1 ? 'file' : 'files'}`;
        },
      },
    ];

    return (
      <Table
        dataSource={flatData}
        columns={columns}
        rowKey="key"
        loading={isLoading}
        pagination={false}
        onRow={(record) => ({
          onClick: () => {
            if (record.type === 'folder' && !record.isEmptyMessage) {
              toggleFolderExpansion(record.id);
            }
          },
          style: { 
            cursor: record.type === 'folder' && !record.isEmptyMessage ? 'pointer' : 'default' 
          }
        })}
        size="small"
        showHeader={true}
      />
    );
  };

  return (
    <div>
      {renderBreadcrumb()}
      
      <div style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3}>
              {selectedFolder ? selectedFolder.name : t('documents:folderBrowser.title')}
            </Title>
            {selectedFolder && (
              <Text type="secondary">
                {selectedFolder.description}
              </Text>
            )}
          </Col>
          <Col>
            <Radio.Group 
              value={viewMode} 
              onChange={(e) => setViewMode(e.target.value)}
              buttonStyle="solid"
            >
              <RadioButton value="folder">
                <AppstoreOutlined /> {t('documents:folderView')}
              </RadioButton>
              <RadioButton value="list">
                <UnorderedListOutlined /> {t('documents:listView')}
              </RadioButton>
            </Radio.Group>
          </Col>
        </Row>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Space>
          <Upload
            beforeUpload={(file) => {
              handleUpload(file);
              return false; // Prevent default upload
            }}
            showUploadList={false}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
          >
            <Button 
              icon={<UploadOutlined />} 
              disabled={currentPath.length === 0}
            >
              {t('documents:actions.uploadDocument')}
            </Button>
          </Upload>
          <Button icon={<PlusOutlined />}>
            {t('documents:actions.createFolder')}
          </Button>
        </Space>
      </div>

      {viewMode === 'folder' ? (
        <>
          <Row gutter={[16, 16]}>
            {folderItems.map(renderFolderCard)}
          </Row>

          {folderItems.length === 0 && (
            <Card style={{ textAlign: 'center', padding: '40px' }}>
              <FileTextOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: 16 }} />
              <Title level={4} type="secondary">
                {t('documents:noDocuments')}
              </Title>
              <Text type="secondary">
                This folder is empty. Documents will appear here when uploaded.
              </Text>
            </Card>
          )}
        </>
      ) : (
        <>
          {renderListView()}
          
          {folderItems.length === 0 && (
            <Card style={{ textAlign: 'center', padding: '40px' }}>
              <FileTextOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: 16 }} />
              <Title level={4} type="secondary">
                {t('documents:noDocuments')}
              </Title>
              <Text type="secondary">
                This folder is empty. Documents will appear here when uploaded.
              </Text>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default FolderBrowser;
